
import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import {
  GammaSpecTypeEnum,
  GammaSpecRnDetecModeEnum,
  GammaSpecRnDetecUnitEnum
} from '@/modules/analysis/models/gammaSpecEnum';
import BatchIdentificationCarousel, { FormSample } from './BatchIdentificationCarousel.vue';
import SpectroRadionuclideInput, { FormRnValue } from './inputs/SpectroRadionuclideInput.vue';
import RadiochemRadionuclideInput, { FormRcRnValue } from './inputs/RadiochemRadionuclideInput.vue';

export interface FormBatch {
  formSamples: FormSample;
  formAnalyzes: FormAnalyzes;
}

interface FormAnalyzes {
  gammaScan: boolean;
  gammaScanTimeValue: number;
  gammaSpec: boolean;
  gammaSpecType: string;
  gammaSpecTimeValue: number;
  gammaSpecRnValue: FormRnValue[];
  radiochem: boolean;
  radiochemRnValue: FormRcRnValue[];
  alphaEmitters: boolean;
  betaEmitters: boolean;
  strontium: boolean;
}

@Component({
  components: { BatchIdentificationCarousel, SpectroRadionuclideInput, RadiochemRadionuclideInput }
})
export default class NewSampleBatchDialog extends Vue {
  protected readonly GammaSpecTypeEnum = GammaSpecTypeEnum;
  protected readonly GammaSpecRnDetecModeEnum = GammaSpecRnDetecModeEnum;

  @Prop({ required: false })
  protected value?: FormBatch;
  protected formAnalyzes: FormAnalyzes = NewSampleBatchDialog.getEmptyFormAnalyzes();
  protected formSample: FormSample = NewSampleBatchDialog.getEmptyFormSample();
  protected isValidFormSample = false;
  protected displayErrorSampleForm = false;
  protected displayErrorAnalyzesForm = false;

  @Watch('value', { immediate: true })
  protected onValueChange(newValue: FormBatch): void {
    if (newValue) {
      this.formAnalyzes = { ...newValue.formAnalyzes };
      this.isValidFormSample = false;
      this.formSample = { ...newValue.formSamples };
    }
  }

  /**
   * BatchIdentificationCarousel has emitted a "form complete" event.
   */
  protected handleFormSample(value: FormSample): void {
    this.formSample = value;
  }

  /**
   * Radionuclide gamma spectrometry input
   */
  protected onRadionuclideInput(value: FormSample, index: number): void {
    Object.assign(this.formAnalyzes.gammaSpecRnValue[index], value);
  }

  /**
   * Radionuclide radiochemistry input
   */
  protected onRcRadionuclideInput(value: FormSample, index: number): void {
    Object.assign(this.formAnalyzes.radiochemRnValue[index], value);
  }

  /**
   * The user clicked the "Save" button. Validate form data and emit the data to parent.
   */
  protected handleSave(): void {
    this.displayErrorSampleForm = false;
    this.displayErrorAnalyzesForm = false;
    if (
      this.formSample !== undefined &&
      this.formSample.samples.length > 0 &&
      this.isValidFormAnalyzes &&
      this.isValidFormSample
    ) {
      const batch: FormBatch = { formSamples: this.formSample, formAnalyzes: this.formAnalyzes };
      this.$emit('create', batch);
      this.formAnalyzes = NewSampleBatchDialog.getEmptyFormAnalyzes();
      this.formSample = NewSampleBatchDialog.getEmptyFormSample();
      if (this.$refs.formAnalyzes) {
        (this.$refs.formAnalyzes as HTMLFormElement).resetValidation();
      }
    } else {
      if (!this.isValidFormAnalyzes) {
        this.displayErrorAnalyzesForm = true;
      }
      if (!this.isValidFormSample || this.formSample === undefined || this.formSample.samples.length === 0) {
        this.displayErrorSampleForm = true;
      }
    }
  }

  /**
   * At least one analysis is selected.
   */
  protected get isValidFormAnalyzes(): boolean {
    let isValidGammaSpec = false;
    if (this.formAnalyzes.gammaSpecType === GammaSpecTypeEnum.TIME && this.formAnalyzes.gammaSpecTimeValue > 0) {
      // User has chosen time spectrometry
      isValidGammaSpec = true;
    } else if (this.formAnalyzes.gammaSpecType === GammaSpecTypeEnum.RN_DETECT) {
      // User has chosen radionuclide detect, at least one radionuclide has to be selected with a detection value
      for (const rn of this.formAnalyzes.gammaSpecRnValue) {
        if (rn.isActive && rn.value !== '' && rn.detectionMode !== '' && rn.detectionUnit !== '') {
          isValidGammaSpec = true;
        } else if (!rn.isActive) {
          continue;
        } else {
          isValidGammaSpec = false;
          break;
        }
      }
    } else if (
      this.formAnalyzes.gammaSpecType === GammaSpecTypeEnum.SCANNING &&
      this.formAnalyzes.gammaScanTimeValue > 0
    ) {
      // User has chosen gamma scanning
      isValidGammaSpec = true;
    }

    let isValidRadiochem = false;
    // Radiochemistry: User has chosen radionuclide detect, at least one radionuclide has to be selected with a detection value
    for (const rn of this.formAnalyzes.radiochemRnValue) {
      if (rn.isActive && rn.value !== '' && rn.detectionMode !== '' && rn.detectionUnit !== '') {
        isValidRadiochem = true;
      } else if (!rn.isActive) {
        continue;
      } else {
        isValidRadiochem = false;
        break;
      }
    }

    if ((this.formAnalyzes.gammaSpec && !isValidGammaSpec) || (this.formAnalyzes.radiochem && !isValidRadiochem)) {
      return false;
    }
    return (
      isValidGammaSpec ||
      isValidRadiochem ||
      this.formAnalyzes.alphaEmitters ||
      this.formAnalyzes.betaEmitters ||
      this.formAnalyzes.strontium
    );
  }

  /**
   * Get gamma spectrometry carousel step.
   */
  protected get gammaSpecTypeCarouselStep(): number {
    let step = 0;

    if (this.formAnalyzes.gammaSpecType === GammaSpecTypeEnum.TIME) {
      step = 0;
    } else if (this.formAnalyzes.gammaSpecType === GammaSpecTypeEnum.RN_DETECT) {
      step = 1;
    } else if (this.formAnalyzes.gammaSpecType === GammaSpecTypeEnum.SCANNING) {
      step = 2;
    }

    return step;
  }

  protected static getEmptyFormAnalyzes(): FormAnalyzes {
    const rnValues = [
      {
        isActive: false,
        label: 'co60',
        value: '',
        detectionMode: GammaSpecRnDetecModeEnum.THRESHOLD,
        detectionUnit: GammaSpecRnDetecUnitEnum.BQ_PER_KG
      },
      {
        isActive: false,
        label: 'cs137',
        value: '',
        detectionMode: GammaSpecRnDetecModeEnum.THRESHOLD,
        detectionUnit: GammaSpecRnDetecUnitEnum.BQ_PER_KG
      },
      {
        isActive: false,
        label: 'eu152',
        value: '',
        detectionMode: GammaSpecRnDetecModeEnum.THRESHOLD,
        detectionUnit: GammaSpecRnDetecUnitEnum.BQ_PER_KG
      },
      {
        isActive: false,
        label: 'pb214',
        value: '',
        detectionMode: GammaSpecRnDetecModeEnum.THRESHOLD,
        detectionUnit: GammaSpecRnDetecUnitEnum.BQ_PER_KG
      },
      {
        isActive: false,
        label: 'bi214',
        value: '',
        detectionMode: GammaSpecRnDetecModeEnum.THRESHOLD,
        detectionUnit: GammaSpecRnDetecUnitEnum.BQ_PER_KG
      },
      {
        isActive: false,
        label: 'ra226',
        value: '',
        detectionMode: GammaSpecRnDetecModeEnum.THRESHOLD,
        detectionUnit: GammaSpecRnDetecUnitEnum.BQ_PER_KG
      },
      {
        isActive: false,
        label: 'ac228',
        value: '',
        detectionMode: GammaSpecRnDetecModeEnum.THRESHOLD,
        detectionUnit: GammaSpecRnDetecUnitEnum.BQ_PER_KG
      },
      {
        isActive: false,
        label: 'th234',
        value: '',
        detectionMode: GammaSpecRnDetecModeEnum.THRESHOLD,
        detectionUnit: GammaSpecRnDetecUnitEnum.BQ_PER_KG
      },
      {
        isActive: false,
        label: 'u235',
        value: '',
        detectionMode: GammaSpecRnDetecModeEnum.THRESHOLD,
        detectionUnit: GammaSpecRnDetecUnitEnum.BQ_PER_KG
      },
      {
        isActive: false,
        label: 'u238',
        value: '',
        detectionMode: GammaSpecRnDetecModeEnum.THRESHOLD,
        detectionUnit: GammaSpecRnDetecUnitEnum.BQ_PER_KG
      }
    ];
    const rcRnValues = [
      {
        isActive: false,
        label: 'th228',
        value: '',
        detectionMode: GammaSpecRnDetecModeEnum.THRESHOLD,
        detectionUnit: GammaSpecRnDetecUnitEnum.BQ_PER_KG
      },
      {
        isActive: false,
        label: 'th230',
        value: '',
        detectionMode: GammaSpecRnDetecModeEnum.THRESHOLD,
        detectionUnit: GammaSpecRnDetecUnitEnum.BQ_PER_KG
      },
      {
        isActive: false,
        label: 'th232',
        value: '',
        detectionMode: GammaSpecRnDetecModeEnum.THRESHOLD,
        detectionUnit: GammaSpecRnDetecUnitEnum.BQ_PER_KG
      },
      {
        isActive: false,
        label: 'u234',
        value: '',
        detectionMode: GammaSpecRnDetecModeEnum.THRESHOLD,
        detectionUnit: GammaSpecRnDetecUnitEnum.BQ_PER_KG
      },
      {
        isActive: false,
        label: 'u235',
        value: '',
        detectionMode: GammaSpecRnDetecModeEnum.THRESHOLD,
        detectionUnit: GammaSpecRnDetecUnitEnum.BQ_PER_KG
      },
      {
        isActive: false,
        label: 'u238',
        value: '',
        detectionMode: GammaSpecRnDetecModeEnum.THRESHOLD,
        detectionUnit: GammaSpecRnDetecUnitEnum.BQ_PER_KG
      },
      {
        isActive: false,
        label: 'pu238',
        value: '',
        detectionMode: GammaSpecRnDetecModeEnum.THRESHOLD,
        detectionUnit: GammaSpecRnDetecUnitEnum.BQ_PER_KG
      },
      {
        isActive: false,
        label: 'pu239240',
        value: '',
        detectionMode: GammaSpecRnDetecModeEnum.THRESHOLD,
        detectionUnit: GammaSpecRnDetecUnitEnum.BQ_PER_KG
      },
      {
        isActive: false,
        label: 'pu241',
        value: '',
        detectionMode: GammaSpecRnDetecModeEnum.THRESHOLD,
        detectionUnit: GammaSpecRnDetecUnitEnum.BQ_PER_KG
      },
      {
        isActive: false,
        label: 'am241',
        value: '',
        detectionMode: GammaSpecRnDetecModeEnum.THRESHOLD,
        detectionUnit: GammaSpecRnDetecUnitEnum.BQ_PER_KG
      },
      {
        isActive: false,
        label: 'np237',
        value: '',
        detectionMode: GammaSpecRnDetecModeEnum.THRESHOLD,
        detectionUnit: GammaSpecRnDetecUnitEnum.BQ_PER_KG
      },
      {
        isActive: false,
        label: 'sr90y90',
        value: '',
        detectionMode: GammaSpecRnDetecModeEnum.THRESHOLD,
        detectionUnit: GammaSpecRnDetecUnitEnum.BQ_PER_KG
      }
    ];

    return {
      gammaScan: false,
      gammaScanTimeValue: 0,
      gammaSpec: false,
      gammaSpecType: GammaSpecTypeEnum.TIME,
      gammaSpecTimeValue: 0,
      gammaSpecRnValue: rnValues,
      radiochem: false,
      radiochemRnValue: rcRnValues,
      alphaEmitters: false,
      betaEmitters: false,
      strontium: false
    };
  }

  protected static getEmptyFormSample(): FormSample {
    return {
      nature: null,
      count: null,
      samples: []
    };
  }

  protected get gammaSpecTimeValueOptions(): { text: string; value: number }[] {
    return [
      {
        text: this.$i18n.t('common.gammaSpecTimeValues.15').toString(),
        value: 15
      },
      {
        text: this.$i18n.t('common.gammaSpecTimeValues.30').toString(),
        value: 30
      },
      {
        text: this.$i18n.t('common.gammaSpecTimeValues.60').toString(),
        value: 60
      },
      {
        text: this.$i18n.t('common.gammaSpecTimeValues.120').toString(),
        value: 120
      },
      {
        text: this.$i18n.t('common.gammaSpecTimeValues.240').toString(),
        value: 240
      },
      {
        text: this.$i18n.t('common.gammaSpecTimeValues.360').toString(),
        value: 360
      },
      {
        text: this.$i18n.t('common.gammaSpecTimeValues.480').toString(),
        value: 480
      },
      {
        text: this.$i18n.t('common.gammaSpecTimeValues.900').toString(),
        value: 900
      },
      {
        text: this.$i18n.t('common.gammaSpecTimeValues.1440').toString(),
        value: 1440
      },
      {
        text: this.$i18n.t('common.gammaSpecTimeValues.1800').toString(),
        value: 1800
      }
    ];
  }

  protected get gammaScanTimeValueOptions(): { text: string; value: number }[] {
    return [
      {
        text: this.$i18n.t('common.gammaScanTimeValues.05').toString(),
        value: 0.5
      },
      {
        text: this.$i18n.t('common.gammaScanTimeValues.1').toString(),
        value: 1
      },
      {
        text: this.$i18n.t('common.gammaScanTimeValues.2').toString(),
        value: 2
      },
      {
        text: this.$i18n.t('common.gammaScanTimeValues.3').toString(),
        value: 3
      },
      {
        text: this.$i18n.t('common.gammaScanTimeValues.5').toString(),
        value: 5
      },
      {
        text: this.$i18n.t('common.gammaScanTimeValues.10').toString(),
        value: 10
      },
      {
        text: this.$i18n.t('common.gammaScanTimeValues.30').toString(),
        value: 30
      }
    ];
  }
}
